import { getFilesURLs, newLoadFiles } from '~src/services/api';
import { DownloadFolderParams } from '~src/utils/downloader/types/types';
import { buildFolderWithPathStructure } from '~src/utils/downloader/shared';
import streamSaver from 'streamsaver';
import * as zip from '@zip.js/zip.js';
import { DownloadingState, useStore } from '~src/store';
import { Modal } from 'antd';

// streamSaver.mitm = 'https://staging.c-web.dk/mitm.html';

class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  private successLog: string[] = [];
  public errorLog: string[] = [];
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  private totalSize = 0;
  private startTime = 0;
  public endTime = 0;
  public fileMap: Map<string, number> = new Map();
  public isAborted = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Project-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isAborted = true;
    }
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;

    // Extract base name and extension
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath; // File name without (x)
    const extension = match?.[3] || ''; // File extension (e.g., .jpg)

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    // Store the new name to prevent future conflicts
    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(fullPath: string, blob: Blob) {
    if (this.isAborted || !this.zipWriter) return;

    const uniquePath = this.getUniqueFileName(fullPath);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniquePath, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniquePath} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      this.errorLog.push(
        `${uniquePath} (0.00 MB) - ERROR: ${error?.message} | D: Folder`
      );
      useStore
        .getState()
        .addError(
          `${uniquePath} (0.00 MB) - ERROR: ${error?.message} | D: Folder`
        );
      this.isAborted = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isAborted || !this.zipWriter) return;

    try {
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));

      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {
      this.errorLog.push(
        `${folderPath} - ERROR: Failed to create empty folder`
      );
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isAborted) return;

      if (this.endTime !== 0) {
        return;
      }

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });

      if (!this.fileMap.has('download_log.txt')) {
        await this.zipWriter.add(
          'download_log.txt',
          new zip.BlobReader(logBlob)
        );
        this.fileMap.set('download_log.txt', 1);
      }

      await this.zipWriter.close();

      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    } catch (error) {
      this.isAborted = true;
    }
  }
}

const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }

              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              console.error(\`Worker Error - File: \${file.file_name}, Error: \${error.message}\`);
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

const ModalConfirmDownload = (): Promise<boolean> => {
  function onCancelfunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content:
        'Selected download folder(s) is too big (500+ files). There can be problems with downloading. We recommend downloading manually if something goes wrong.',
      onOk: () => resolve(true),
      onCancel: () => {
        onCancelfunction();
        resolve(false);
      }, // User cancels
      okText: 'I understand',
      cancelText: 'Cancel',
    });
  });
};

const findAllEmptyFoldersWithPaths = (
  folderId: number,
  allFolders: any[],
  rootFolderName: string
): { name: string; path: string }[] => {
  const emptyFolders: { name: string; path: string }[] = [];
  const stack: { id: number; path: string }[] = [
    { id: folderId, path: rootFolderName },
  ];

  while (stack.length > 0) {
    const poppedItem = stack.pop();
    if (!poppedItem) continue; // 🔹 Ensure `poppedItem` is always defined

    const { id: currentId, path: currentPath } = poppedItem;
    const children = allFolders.filter(
      (folder) => folder.parent_id === currentId
    );

    let hasFiles = false;
    for (const child of children) {
      const childPath = `${currentPath}/${child.name}`;
      if (child.is_empty) {
        emptyFolders.push({ name: child.name, path: childPath });
      } else {
        hasFiles = true;
      }
      stack.push({ id: child.id, path: childPath });
    }

    // 🔹 Ensure all non-empty folders are added too
    if (!hasFiles && !emptyFolders.some((f) => f.path === currentPath)) {
      emptyFolders.push({
        name: currentPath.split('/').pop() || '',
        path: currentPath,
      });
    }
  }

  return emptyFolders;
};

export const downloadFolder = async ({
  folder,
  allFolders,
  fileVersionsContext,
  onDownloadStarted,
}: DownloadFolderParams): Promise<void> => {
  try {
    const structure = buildFolderWithPathStructure(folder, allFolders);

    const folderId = folder.id;
    const rootFolderName = folder.name;

    const emptyFolders = findAllEmptyFoldersWithPaths(
      folderId,
      allFolders,
      rootFolderName
    );

    const files = await newLoadFiles('files', {
      projectId: folder.project_id,
      folderId: folder.id,
      serial: 1,
    });

    let startDownload = true;

    if (files.length > 500) {
      startDownload = await ModalConfirmDownload();
    }

    if (!startDownload) {
      return;
    }

    const totalSize = files.reduce((acc, file) => acc + file.file_size, 0);

    const zipStream = new ZipStream(files.length, totalSize);
    useStore.getState().totalDownloadFiles(files.length);
    zipStream.start();

    if (zipStream.isAborted) {
      return;
    }

    for (const { path } of emptyFolders) {
      await zipStream.addEmptyFolder(path);
    }

    if (!files.length) {
      await zipStream.finish();
      return;
    }

    const workers: Worker[] = [];
    const fileQueue = [...files];
    let activeWorkers = 0;
    const NUM_WORKERS = 5;

    for (let i = 0; i < NUM_WORKERS; i++) {
      workers.push(createWorker());
    }

    const processFile = async (file) => {
      const [urlData] = await getFilesURLs(String(folder.project_id), [
        file.using_key,
      ]);

      const fileURL = urlData?.url;
      if (!fileURL) {
        zipStream.errorLog.push(`${file.file_name} - ERROR: Missing URL`);
        useStore.getState().addError(`${file.file_name} - ERROR: Missing URL`);
        return null;
      }

      return { ...file, url: fileURL };
    };

    const assignFileToWorker = async (worker: Worker) => {
      if (zipStream.isAborted) {
        return;
      }

      const nextFile = fileQueue.shift();
      if (!nextFile) {
        if (activeWorkers === 0) {
          setTimeout(async () => {
            if (activeWorkers === 0) {
              await zipStream.finish();
              workers.forEach((worker) => worker.terminate());
            }
          }, 500);
        }
        return;
      }

      const processedFile = await processFile(nextFile);
      if (!processedFile) {
        await assignFileToWorker(worker); // Retry another file if missing URL
        return;
      }

      activeWorkers++;

      worker.postMessage({ file: processedFile });

      worker.onmessage = async (event) => {
        try {
          const { success, file, blob, error } = event.data;
          if (!file) {
            return;
          }

          const folderPath =
            structure.find((f) => f.id === file.folder_id)?.path || '';
          const fullPath = `${folderPath}/${file.file_name}`;

          if (success) {
            await zipStream.addFile(fullPath, blob);
          } else {
            zipStream.errorLog.push(`${fullPath} - ERROR: ${error}`);
            useStore.getState().addError(`${fullPath} - ERROR: ${error}`);
          }

          activeWorkers--;

          if (fileQueue.length > 0) {
            await assignFileToWorker(worker);
          } else if (activeWorkers === 0) {
            setTimeout(async () => {
              if (activeWorkers === 0) {
                await zipStream.finish();
                workers.forEach((worker) => worker.terminate());
              }
            }, 500);
          }
        } catch (err) {
          zipStream.errorLog.push(`Worker processing error - ${err.message}`);
          useStore
            .getState()
            .addError(`Worker processing error - ${err.message}`);
        }
      };
    };

    for (const worker of workers) {
      await assignFileToWorker(worker);
    }
  } catch (error) {}
};
